import { computed, onBeforeUnmount, ref, type Ref } from 'vue'

interface UseResizablePaneOptions {
    containerRef: Ref<HTMLElement | null>
    sizePercent: Ref<number>
    minPanePx: number
    maxPanePx?: number
    minContentPx: number
    onChange?: (value: number) => void
}

const clamp = (value: number, min: number, max: number) =>
    Math.min(max, Math.max(min, value))

const roundToTwo = (value: number) => Math.round(value * 100) / 100

export const useResizablePane = ({
    containerRef,
    sizePercent,
    minPanePx,
    maxPanePx,
    minContentPx,
    onChange,
}: UseResizablePaneOptions) => {
    const isDragging = ref(false)

    const cleanup = () => {
        if (!import.meta.client) return
        window.removeEventListener('pointermove', handlePointerMove)
        window.removeEventListener('pointerup', stopDragging)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
    }

    const getSafeBounds = () => {
        const el = containerRef.value
        if (!el) return null

        const rect = el.getBoundingClientRect()
        if (!rect.width) return null

        const minPercent = (minPanePx / rect.width) * 100
        const maxByContentPercent = 100 - (minContentPx / rect.width) * 100
        const maxByPanePercent = maxPanePx
            ? (maxPanePx / rect.width) * 100
            : 100
        const maxPercent = Math.max(
            minPercent,
            Math.min(maxByContentPercent, maxByPanePercent)
        )

        return {
            rect,
            minPercent,
            maxPercent,
        }
    }

    const applyPercent = (nextValue: number) => {
        const bounds = getSafeBounds()
        if (!bounds) return

        const safe = roundToTwo(
            clamp(nextValue, bounds.minPercent, bounds.maxPercent)
        )

        sizePercent.value = safe
        onChange?.(safe)
    }

    const handlePointerMove = (event: PointerEvent) => {
        if (!isDragging.value) return

        const bounds = getSafeBounds()
        if (!bounds) return

        const rawPercent =
            ((event.clientX - bounds.rect.left) / bounds.rect.width) * 100

        applyPercent(rawPercent)
    }

    const stopDragging = () => {
        if (!isDragging.value) return
        isDragging.value = false
        cleanup()
    }

    const startDragging = (event: PointerEvent) => {
        if (!import.meta.client) return
        if (event.button !== 0) return

        event.preventDefault()
        isDragging.value = true
        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'
        window.addEventListener('pointermove', handlePointerMove)
        window.addEventListener('pointerup', stopDragging)
    }

    onBeforeUnmount(() => {
        stopDragging()
    })

    return {
        isDragging: computed(() => isDragging.value),
        applyPercent,
        startDragging,
        stopDragging,
    }
}
